// l10n status: partial
import { action, computed, flow, observable, makeObservable, override } from "mobx"
import { PageAbstract } from "./PageAbstract"
import { rootStore } from "store/RootStore"
import { CleConfig } from "scenes/ChannelListEditor/ChannelListEditor"
import { useNavigateRouteAdd } from "components/utils/RouteUtils"
import { ROUTE_NAMES } from "site-ql/RouteConst"
import { Observe } from "store/page/mixin/ObserveMixin"
import { EventEmitter } from "components/utils/EventEmitter"
import { LiveTvConfig } from "scenes/LiveTv/LiveTv"
import { l10n } from "store/lang/L10n"

export class ChannelListEditor extends PageAbstract {
	// MTVW-503: Use Channels MS
	msEpg = rootStore.page.MsEpg

	scrollTop = 0
	// TODO: change initialization, change to computed?
	listChannelIds = []
	channelLists = []
	allChannelLists = []
	listName = ""
	initialListName = ""
	searchString = ""
	filteredChannels = []
	currentChannelListId = "allChannelsListId"
	currentChannels = []
	editListId = null
	// MTVW-1098: always public
	isPersonalList = false

	_observer = new Observe(this, "_observer")
	readyCallback = null
	channelAddedCallback = null
	listUpdatePosition = 0
	dragChannel = -1
	// MTVW-657
	MAX_NAME_LENGTH = 50

	constructor(parent, path) {
		super(parent, path)
		makeObservable(this, {
			scrollTop: observable,
			listChannelIds: observable,
			channelLists: observable,
			allChannelLists: observable,
			listName: observable,
			searchString: observable,
			filteredChannels: observable,
			currentChannels: observable,
			currentChannelListId: observable,
			editListId: observable,
			isPersonalList: observable,
			listUpdatePosition: observable,
			dragChannel: observable,
			_init: action,
			getCurrentChannels: action,
			_setupListAndName: action,
			loadChannelLists: action,
			// MTV-3583: do not make it computed here, mobx 6 generates an error if it's already registered in the base class
			//isReady: computed,
			setCurrentChannelListId: action,
			assignUniqueChannelListName: action,
			validateChannelListName: action,
			// change for mobx 6.3 which is more strict
			//setRouteParams: action,
			setRouteParams: override,
			trimListName: action,
			saveChannelList: action,
			deleteChannelList: action,
			toggle: action,
			addChannel: action,
			remove: action,
			removeAll: action,
			selectAll: action,
			filterChannels: action,
			setDragChannel: action,
			setVerticalScroll: action,
			setListChannelIds: action
		})
	}

	// TODO
	navigateRouteAdd = useNavigateRouteAdd()

	_refElemScroll = null

	_initAsync = flow(function* () {
		//console.debug("initAsync")
		const start = new Date().getTime()
		// get also adult channels
		yield rootStore.channelsService.getChannelsAsync(true)
		const elapsed = new Date().getTime() - start
		console.debug("isReady %s elapsed %s", this.isReady, elapsed)
		yield this._setupListAndName()
	})

	_onMount() {
	}

	_init = flow(function* (id) {
		//console.debug("_init %s", id)
		this.scrollTop = 0
		this.listChannelIds = []
		this.listName = ""
		this.searchString = ""
		this.filteredChannels = []
		this.currentChannelListId = "allChannelsListId"
		this.editListId = null
		// MTVW-1098: always public
		this.isPersonalList = false
		this._refElemScroll = null
		this.listUpdatePosition = 0
		yield this._initAsync()
	})

	_setupListAndName = flow(function* () {
		this.editListId = this.routeParams.type === "edit" ? this.routeParams.id : null
		//console.debug("_setupListAndName %s", this.editListId)
		yield this.loadChannelLists()
		if (this.editListId !== null) {
			// edit mode
			// traxis cleanup: use msEpg
			// MTVW-1013: signal traxisObj
			const channels = yield rootStore.channelsService.getChannelsForList(this.editListId)
			//console.debug("getChannelsForList 1", /*this.msEpg.isReady,*/ channels)
			//console.debug("list %o", list)
			this.listChannelIds = []
			for (let i = 0; i < channels?.length; i++) {
				this.listChannelIds.push(channels[i].id)
			}
			//console.debug("listChannelIds %o", this.listChannelIds)

			//this.setCurrentChannelListId(this.editListId)
			const chList = yield rootStore.channelsService.getChannelListAsync(this.editListId)
			//console.debug("getChannelList", chList)
			this.listName = chList?.name
			this.initialListName = this.listName
			// MTVW-1098: always public
			this.isPersonalList = false
			//console.debug("edit channels length %s name %s personal", channels.length, this.listName, this.isPersonalList)
			this.listUpdatePosition = 0
			//console.debug("isCreator %s %s %s", isCreator, chList?.ProfileId, rootStore.sso.profile.id)
			if (this.readyCallback) this.readyCallback(this.listName, this.isPersonalList, false)
		}
		else {
			this.listChannelIds = ["2021", "2023"] // preset to first 2 elements for creating a new list
			//this.channelLists = yield rootStore.channelsService.getChannelListsAsync(true)
			this.assignUniqueChannelListName(l10n.featureChannelListsTitle)
			//console.debug("init channels for create")
			this.listUpdatePosition = 0
			if (this.readyCallback) this.readyCallback(this.listName, this.isPersonalList, true)
		}
	})

	assignUniqueChannelListName = (name,) => {
		// MTVW-657
		let validStr = this._getValidName(name)

		let unique = false
		while (!unique) {
			unique = this.isUniqueChannelListName(validStr)
			if (!unique) {
				const match = validStr.match(/\d+$/)
				if (!match) {
					validStr += " 1"
				}
				else {
					const num = parseInt(match)
					validStr = validStr.substring(0, validStr.length - match.length) + (num + 1)
				}
			}
		}
		this.listName = validStr
	}

	isUniqueChannelListName = (name = this.listName) => {
		//MTVW-1098: Note that CoCo returns only public channel lists here,
		// getChannelListsAsync will fetch also all channel lists
		const allChannelLists = rootStore.channelsService.getAllChannelLists()?.ChannelLists?.ChannelList
		if (allChannelLists?.length === 0) return true
		const filtered = allChannelLists?.filter(i => { return i.Name.toLowerCase() === name.toLowerCase() })
		if (this.editListId === null) {
			// must be unique if new channel list is created
			return filtered?.length === 0
		}
		// editing cases
		else if (filtered?.length === 1 && this.editListId === filtered[0]?.id) {
			// name has not been changed
			return true
		}
		else if (filtered?.length === 0) {
			// update the name of the channel list being edited
			return true
		}
		else {
			return false
		}
	}

	_getValidName(name, maxLength = this.MAX_NAME_LENGTH) {
		// strip off invalid characters and limit string length
		//0-9, A-Z, a-z, ä, Ä, ü, Ü, ö, Ö, à, À, â, Â, é, É, è, È, ê, Ê, ë, Ë, î, Î, ï, Ï, ô, Ô, û, Û, ù, Ù, ì, Ì, ò, Ò, ó, Ó, ÿ, Ÿ, ç, Ç, œ, Œ, æ, Æ, ñ, Ñ, (, ), -, _, !, ', [space]
		// MTVW-1098: trimming
		return name.replace(/[^a-zA-Z0-9 äÄüÜöÖàÀâÂéÉèÈêÊëËîÎïÏôÔûÛùÙìÌòÒóÓÿŸçÇœŒæÆñÑ()_!-]+/g, "")
			// remove leading spaces
			.replace(/^\s+/, "")
			// collapse multiple spaces anywhere into one
			.replace(/\s{2,}/g, " ")
			// max length
			.substring(0, maxLength)
	}

	validateChannelListName = flow(function* (name = this.listName) {
		// MTVW-313, TODO: Requires fix in CoCo
		//return yield rootStore.channelsService.validateChannelListNameAsync(name)
		const validStr = this._getValidName(name)
		this.listName = validStr
		//console.debug("validate %s %s", name, validStr)
		return (validStr?.length >= 2) && (validStr?.length <= this.MAX_NAME_LENGTH)
	})

	setRouteParams(params) {
		super.setRouteParams(params)
		console.debug("routeParams %s %s", this.routeParams.type, this.routeParams.id)
		//this._init()
		if ((this.routeParams.type !== "edit") && (this.routeParams.type !== "create")) {
			// TODO: 401
			this.navigateRouteAdd(ROUTE_NAMES.NotFound)
		}
		this._setupListAndName()
		return this
	}

	get isReady() {
		return true //this._pageStore.ChannelListRepo.isReady
	}

	loadChannelLists = flow(function* () {
		try {
			this.channelLists = yield rootStore.channelsService.getChannelListsAsync(true)
		}
		catch (e) {
			console.error("ChannelListEditor.loadChannelLists %o", e)
		}
	})

	setChannelLists(chLists) {
		this.channelLists = chLists
	}

	setCurrentChannelListId(id) {
		this.currentChannelListId = id
		// will set this.currentChannels
		this.getCurrentChannels()
	}

	getCurrentChannels = flow(function* () {
		let result = []
		if (this.isReady) {
			if (this.searchString?.length > 0) {
				result = this.filteredChannels
			}
			else {
				result = yield rootStore.channelsService.getChannelsForList(this.currentChannelListId)
				//console.debug("getChannelsForList 2", result)
			}
		}
		this.currentChannels = result
		return result
	})

	isSelected(channelId) {
		return computed(() => {
			return this.listChannelIds.includes(channelId)
			//return oEvent.id === rootStore.channelsService.activeChannelId
		}).get()
	}

	toDelete = [
		//"d7d33255-edfd-47a5-a616-d4983c93bdba",
		//"12b081a7-deba-407c-86a1-9ff610e22241",
		//"e421e3bc-db24-421d-919f-1b0e9b40a592"
	]

	trimListName() {
		// MTVW-1098: trimming
		this.listName = this.listName.trim()
	}

	saveChannelList = flow(function* (channelListName = this.listName) {
		try {
			for (let i = 0; i < this.toDelete?.length; i++) {
				yield rootStore.channelsService.deleteChannelListAsync(this.toDelete[i])
			}

			let clListId = this.editListId
			if (clListId === null) {
				// create new channel list
				const req = yield rootStore.channelsService.createChannelListAsync(channelListName)
				clListId = req.id
				this.initialListName = channelListName
			}
			// MTVW-1091: pass channel list name as null if it didn't change! 
			// If we pass the same name this happens: IllegalArgument: Invalid channel list name
			const name = channelListName === this.initialListName ? null : channelListName
			const result = yield rootStore.channelsService.updateChannelListAsync(clListId, name, this.listChannelIds)

			// refresh, kick on fetching current data
			// MTVW-636: refresh channel lists
			EventEmitter.dispatch("reloadChannelLists", "")

			// check whether active channel has been deleted
			/*
			console.debug("saveChannelList", rootStore.channelsService.activeChannelListId, clListId, rootStore.channelsService.activeChannel,
				this.listChannelIds, this.listChannelIds.findIndex(i => i === rootStore.channelsService.activeChannelId)
			)
			*/
			if (rootStore.channelsService.activeChannelListId === clListId) {
				if ((this.listChannelIds.findIndex(i => i === rootStore.channelsService.activeChannelId) < 0) && (this.listChannelIds?.length > 0)) {
					rootStore.channelsService.setActiveChannel(this.listChannelIds[0])
					rootStore.page.LiveTv.setEpgListPos(this.listChannelIds[0], 0)
				}
			}
		}
		catch (error) {
			console.error("saveChannelList", error)
			throw (error)
		}
	})

	deleteChannelList = flow(function* (channelListId = null) {
		if (channelListId !== null) {
			const activeId = rootStore.channelsService.activeChannelListId
			yield rootStore.channelsService.deleteChannelListAsync(channelListId)

			// MTVW-636
			// notify ChannelListMenu in case active channel list has been deleted
			//console.debug("deleteChannelList", activeId, channelListId)
			// reload will be done in ChannelListMenu
			EventEmitter.dispatch("reloadChannelLists", activeId === channelListId ? "deleted" : "")

			rootStore.page.LiveTv.setEpgListPos(rootStore.channelsService.activeChannelId, LiveTvConfig.cellHeight + LiveTvConfig.cellMarginBottom)
		}
	})

	toggle(channelId) {
		const index = this.listChannelIds.indexOf(channelId)
		if (index > -1) {
			this.remove(index)
		} else {
			this.listChannelIds.push(channelId)
			this.setChannelListPos(this.listChannelIds?.length - 1, CleConfig.listCellHeight + CleConfig.listCellMarginBottom)
			if (this.channelAddedCallback) this.channelAddedCallback(this.listChannelIds?.length - 1)
			//this._refElemScroll.scrollTop = this.scrollTop
		}
		//console.debug("channelIds %o", this.listChannelIds)
	}

	addChannel(channelId) {
		// only used for adding a temporary channel during drag, don't invoke channelAddedCallback
		const index = this.listChannelIds.indexOf(channelId)
		if (index < 0) {
			this.listChannelIds.push(channelId)
		}
	}

	remove(index) {
		//this.listChannelIds.splice(index, 1)
		this.listChannelIds = this.listChannelIds.filter(item => { return item !== this.listChannelIds[index] })
		this.listUpdatePosition = (index < this.listChannelIds?.length) ? index : Math.max(0, this.listChannelIds?.length - 1)
	}

	removeAll(list) {
		for (let i = 0; i < list?.length; i++) {
			let index = this.listChannelIds.indexOf(list[i].id)
			if (index > -1) {
				this.remove(index)
			}
		}
	}

	selectAll(list) {
		for (let i = 0; i < list?.length; i++) {
			if (this.listChannelIds.indexOf(list[i].id) < 0) {
				this.listChannelIds.push(list[i].id)
			}
		}
	}

	filterChannels(query) {
		const searchString = query.toLowerCase().replace(/ /gi, "")
		// MTVW-503: Use Channels MS
		const filtered = rootStore.channelsService.channels.filter(i => {
			 return i.name.toLowerCase().replace(/ /gi, "").indexOf(searchString) > -1
		})
		//console.debug("filtered %o", filtered)
		this.filteredChannels = filtered
		this.searchString = searchString
	}

	setDragChannel(channel) {
		this.dragChannel = channel
	}

	getChannel(channelId) {
		// MTVW-503: Use Channels MS
		return rootStore.channelsService.getChannel(channelId)
	}
	getIndex(channelId) {
		return this.listChannelIds.indexOf(channelId)
	}
	setRefElemScroll(refElem) {
		this._refElemScroll = refElem
		if (refElem !== null) {
			this._refElemScroll.scrollTop = this.scrollTop
		}
	}
	setChannelListPos(index, height = null) {
		if (height !== null) {
			// index = rootStore.channelsService.indexInChannelList(channelId)
			this.scrollTop = index > 0 ? index * height : 0
		}
		this.listUpdatePosition = index
	}

	setVerticalScroll() {
		console.log("@()")
		if (this._refElemScroll !== null) {
			this.scrollTop = Math.max(0, this._refElemScroll.scrollTop)
		}
		return this
	}

	setListChannelIds(ids) {
		this.listChannelIds = ids
	}
}
